/**
 * A CSV a spreadsheet opens cleanly: a UTF-8 BOM (so Excel reads Arabic as
 * Arabic), CRLF rows, cells quoted only when they must be.
 *
 * Two traps are closed per cell:
 * - Formula injection. Text starting with = + - @ RUNS as a formula in Excel —
 *   and customers type their own names in the app. Such text gets a leading '
 *   so it stays text.
 * - Number mangling. Excel turns +9647501234567 into 9.64750E+12 and drops the
 *   0 of 0750…. A `{ text }` cell that looks numeric is written as ="…", which
 *   Excel, Sheets and LibreOffice all keep verbatim.
 *
 * Pass amounts as numbers: they are written plain (no grouping, no currency
 * sign) so the sheet can sum them.
 */
export type CsvCell = string | number | null | undefined | { text: string | null | undefined };

function cell(v: CsvCell): string {
  let s: string;
  if (v === null || v === undefined) s = '';
  else if (typeof v === 'number') s = Number.isFinite(v) ? String(v) : '';
  else if (typeof v === 'object') {
    const text = (v.text ?? '').trim();
    s = /^\+?\d[\d\s-]*$/.test(text) ? `="${text}"` : text;
    if (!s.startsWith('="') && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  } else {
    s = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  }
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: CsvCell[][]): string {
  return `\uFEFF${rows.map((row) => row.map(cell).join(',')).join('\r\n')}`;
}

export function downloadCsv(filename: string, rows: CsvCell[][]): void {
  downloadBlob(filename, new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
}

/** Save a file the page built — CSV, xlsx — under `filename`. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // A beat later: revoking in the same tick can cancel the download in some webviews.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Today as YYYY-MM-DD in the browser's own timezone — for file names. */
export function localDateStamp(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
